import type { SlideEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/slide-editor';
import { BrowserCanvas } from '@tencent/alloy-renderer-browser';
import { Canvas } from '@tencent/alloy-renderer';
import { IComponentWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-wrapper.interface';
import { ShapeRenderer } from '@src/slide/application/service/similarity-collection/renderer/shape-renderer';
import { changeVisibleStatus } from '@src/slide/application/service/similarity-collection/utils';
import {
    ISimilarityCollectionServiceDelegates,
} from '@src/slide/application/service/similarity-collection/similarity-collection-service.interface';
import { MasterShapeRenderer } from '@src/slide/application/service/similarity-collection/renderer/master-shape-renderer';
import { Container } from '@tencent/alloy-renderer/dist/scene/container';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import {
    IComponentFactory,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory.interface';

export class SlideRenderer {
    private slideEditor: SlideEditor;
    private readonly services: ISimilarityCollectionServiceDelegates;
    public constructor(services: ISimilarityCollectionServiceDelegates, slideEditor: SlideEditor) {
        this.services = services;
        this.slideEditor = slideEditor;
    }

    public async collectData() {
        await this.updateSlideEditorSize();
        const shapeData: any[] = [];

        this.shapeRendererCollection.forEach((shapeRenderer) => {
            shapeData.push(shapeRenderer.collectData());
        });
        const masterShapeData: any[] = [];
        this.masterShapeRendererCollection.forEach((shapeRenderer) => {
            masterShapeData.push(shapeRenderer.collectData());
        });
        const layoutShapeData: any[] = [];
        this.layoutShapeRendererCollection.forEach((shapeRenderer) => {
            layoutShapeData.push(shapeRenderer.collectData());
        });

        return {
            slideShapes: shapeData,
            masterShapes: masterShapeData,
            layoutShapes: layoutShapeData,
            count: shapeData.length + masterShapeData.length + layoutShapeData.length,
        };
    }

    public async collectImage(params) {
        const { needSlideImg = true, needShapeImg = true } = params;
        await this.updateSlideEditorSize();
        const shapeData: any[] = [];
        const masterShapeData: any[] = [];
        const layoutShapeData: any[] = [];
        let image;

        if (needSlideImg) {
            image = await this.export();
        }
        if (needShapeImg) {
            this.shapeRendererCollection.forEach((shapeRenderer) => {
                shapeData.push(shapeRenderer.collectImage());
            });
            this.masterShapeRendererCollection.forEach((shapeRenderer) => {
                masterShapeData.push(shapeRenderer.collectImage());
            });
            this.layoutShapeRendererCollection.forEach((shapeRenderer) => {
                layoutShapeData.push(shapeRenderer.collectImage());
            });
        }

        return {
            image,
            slideShapes: shapeData,
            masterShapes: masterShapeData,
            layoutShapes: layoutShapeData,
        };
    }

    public async export(type?: string) {
        const canvasElement = document.createElement('canvas');
        const canvas = new BrowserCanvas({
            native: canvasElement,
            bindEvents: false,
        });
        const { slideWidth, slideHeight } = this.services.uiDataService.presentation.props;
        const { scale } = this.services.editAreaCanvasSizeManager.getCanvasSize();
        canvas.width = Math.round(slideWidth * scale);
        canvas.height = Math.round(slideHeight * scale);

        await this.renderStableLayer(canvas);
        await this.renderSlideComponent(canvas);

        return canvas.toDataUrl(type);
    }

    public get shapeRendererCollection(): ShapeRenderer[] {
        const elements = this.slideEditor.slideComponent.elementsLayer.children;
        const shapeRendererArr: ShapeRenderer[] = [];
        elements.forEach((element: IComponentWrapper) => {
            const shapeRenderer = new ShapeRenderer(this.services, element as IComponentWrapper);
            shapeRendererArr.push(shapeRenderer);
        });
        return shapeRendererArr;
    }

    public get masterShapeRendererCollection(): MasterShapeRenderer[] {
        const elementsOrigin = this.slideEditor.getMasterAppService()?.getComponent().children || [];
        const elementsCanChange = this.slideEditor.slideComponent.getMasterVariableElementsLayer()?.children || [];
        const elements = this.correctElementsSort(
            this.slideEditor.getMasterAppService()?.getComponent()
                .getSlideMode().children,
            [...elementsOrigin, ...elementsCanChange] as unknown as IComponentFactory[],
        );
        const showMasterSp = this.slideEditor.slide?.getRelationLayoutModel()?.showMasterSp;
        const showMasterAndLayoutSp = this.slideEditor.slide?.showMasterSp;
        if (!elements || !(showMasterSp && showMasterAndLayoutSp)) {
            return [];
        }
        const shapeRendererArr: MasterShapeRenderer[] = [];
        elements.forEach((element: IComponentWrapper) => {
            const { x = 0, y = 0 } = this.slideEditor.getStableLayerService()?.container || {};
            const { scale } = this.services.editAreaCanvasSizeManager.getCanvasSize();
            const slideLeft = x * scale;
            const slideTop = y * scale;
            const shapeRenderer = new MasterShapeRenderer(this.services, element, slideLeft, slideTop);
            shapeRendererArr.push(shapeRenderer);
        });
        return shapeRendererArr;
    }

    public get layoutShapeRendererCollection(): MasterShapeRenderer[] {
        const elementsOrigin = this.slideEditor.getLayoutAppService()?.getComponent().children || [];
        const elementsCanChange = this.slideEditor.slideComponent.getLayoutVariableElementsLayer()?.children || [];
        const elements = this.correctElementsSort(
            this.slideEditor.getLayoutAppService()?.getComponent()
                .getSlideMode().children,
            [...elementsOrigin, ...elementsCanChange] as unknown as IComponentFactory[],
        );
        const showMasterAndLayoutSp = this.slideEditor.slide?.showMasterSp;
        if (!elements || !showMasterAndLayoutSp) {
            return [];
        }
        const shapeRendererArr: MasterShapeRenderer[] = [];
        elements.forEach((element: IComponentWrapper) => {
            const { x = 0, y = 0 } = this.slideEditor.getStableLayerService()?.container || {};
            const { scale } = this.services.editAreaCanvasSizeManager.getCanvasSize();
            const slideLeft = x * scale;
            const slideTop = y * scale;
            const shapeRenderer = new MasterShapeRenderer(this.services, element, slideLeft, slideTop);
            shapeRendererArr.push(shapeRenderer);
        });
        return shapeRendererArr;
    }

    private updateSlideEditorSize() {
        const { slideWidth, slideHeight } = this.services.uiDataService.presentation.props;
        const { scale } = this.services.editAreaCanvasSizeManager.getCanvasSize();

        this.slideEditor.updateInfo(scale, {
            width: slideWidth,
            height: slideHeight,
        } as any, {
            width: slideWidth,
            height: slideHeight,
        } as any);
        this.slideEditor.getBackgroundAppService()?.updateWithData?.(scale);
        this.slideEditor.getMasterAppService()?.updateWithData?.(scale);
        this.slideEditor.getLayoutAppService()?.updateWithData?.(scale);
        this.slideEditor.getStableLayerService?.()?.updateInfo(scale);
        this.slideEditor.getStableLayerService?.()?.render({
            slideModel: this.slideEditor.slide,
            masterModel: this.slideEditor.slide.getRelationMasterModel(),
            layoutModel: this.slideEditor.slide.getRelationLayoutModel(),
        });
        this.slideEditor.mountSlide();
    }

    private async renderStableLayer(canvas: BrowserCanvas) {
        await this.slideEditor.getBackgroundAppService()?.ensureRenderComplete();
        await this.slideEditor.getMasterAppService()?.ensureRenderComplete();
        await this.slideEditor.getLayoutAppService()?.ensureRenderComplete();
        const stableLayer = this.slideEditor.getStableLayerService();
        stableLayer?.render({
            slideModel: this.slideEditor.slide,
            masterModel: this.slideEditor.slide.getRelationMasterModel(),
            layoutModel: this.slideEditor.slide.getRelationLayoutModel(),
        });
        const stableLayerCanvas = stableLayer?.getApplication().view as Canvas<HTMLCanvasElement>;
        if (stableLayerCanvas) {
            const scale = this.services.editAreaCanvasSizeManager.getCanvasScale();
            canvas.drawCanvas(
                stableLayerCanvas,
                -(stableLayer?.container?.x ?? 0) * scale,
                -(stableLayer?.container?.y ?? 0) * scale,
            );
        }
    }

    private async renderSlideComponent(canvas: BrowserCanvas) {
        const { slideLeft, slideTop } = this.services.editAreaCanvasSizeManager.getCanvasSize();
        const slideComponent = this.slideEditor?.slideComponent;
        if (!slideComponent) {
            return;
        }

        // 让canvas层gif、附件可见，无文本placeholder不可见
        changeVisibleStatus((slideComponent?.elementsLayer?.children || []) as IComponentWrapper[], true);
        await slideComponent.ensureRenderComplete();
        canvas.drawCanvas(this.slideEditor.application.view, -slideLeft, -slideTop);
        // 恢复gif、附件、无文本placeholder的原本状态
        changeVisibleStatus((slideComponent?.elementsLayer?.children || []) as IComponentWrapper[]);
        this.slideEditor.application.requestRender();
    }

    // 因为master layout上复制的页码这种形状，单独拆出来放在了slideComponent的一个节点上，再拼回去顺序就乱了，所以需要纠正
    private correctElementsSort(modelArr?: IElementBaseModel[], componentArr?: IComponentFactory[]): Container[] {
        if (!modelArr || !componentArr) {
            return [];
        }
        const newArr: Container[] = [];
        const componentMap = {};
        componentArr.forEach((component) => {
            componentMap[component.factoryOptions.model.key] = component;
        });
        modelArr.forEach((model) => {
            if (componentMap[model.key]) {
                newArr.push(componentMap[model.key]);
            }
        });
        return newArr;
    }
}
